import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm, useWatch, type Resolver } from 'react-hook-form';
import { Button, CustomSelect, RichTextEditor, SaveDraftModal } from '@/components/ui';
import { Heading, Label, Paragraph } from '@/components/ui/Typography';
import Input from '@/components/ui/Input/Input';
import FileUpload from '@/components/ui/FileUpload';
import { useCreateHistory, useUpdateHistory, useHistory } from '@/lib/api/hooks/history';
import { useFileUpload } from '@/lib/api/hooks/file-upload';
import { showToast } from '@/lib/toast';
import { ChevronLeftIcon } from '@/components/icons';

interface HistoryFormData {
  year: string;
  title: string;
  description: string;
  imageUrl: string;
}

const DEFAULT_FORM_VALUES: HistoryFormData = {
  year: String(new Date().getFullYear()),
  title: '',
  description: '',
  imageUrl: ''
};

const htmlToPlainText = (value: string): string =>
  value?.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&[a-z]+;/gi, '').trim() || '';

const BaseHistorySchema = z.object({
  year: z
    .string()
    .min(1, 'Year is required')
    .refine((value) => {
      const numericYear = Number(value);
      return Number.isInteger(numericYear) && numericYear >= 1900 && numericYear <= 2100;
    }, 'Year must be between 1900 and 2100'),
  title: z.string(),
  description: z.string(),
  imageUrl: z.string()
});

const PublishHistorySchema = BaseHistorySchema.superRefine((data, ctx) => {
  if (!data.title.trim()) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['title'], message: 'Title is required' });
  }

  if (!htmlToPlainText(data.description)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['description'], message: 'Description is required' });
  }

  if (!data.imageUrl.trim()) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['imageUrl'], message: 'History image is required' });
  }
});

const HistoryEditor: React.FC = () => {
  const { historyId } = useParams<{ historyId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const isEditMode = Boolean(historyId);
  const existingHistory = location.state?.history;
  const submitIntentRef = useRef<'DRAFT' | 'PUBLISH'>('DRAFT');
  const fileUploadRef = useRef<HTMLDivElement>(null);

  const [localCoverFile, setLocalCoverFile] = useState<File | null>(null);
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string>('');
  const [existingCoverUrl, setExistingCoverUrl] = useState<string>('');
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isDraftSaving, setIsDraftSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  const draftResolver = zodResolver(BaseHistorySchema);
  const publishResolver = zodResolver(PublishHistorySchema);

  const resolver: Resolver<HistoryFormData> = (values, context, options) => {
    if (submitIntentRef.current === 'PUBLISH') {
      return publishResolver(values, context, options);
    }

    return draftResolver(values, context, options);
  };

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isDirty, isSubmitting }
  } = useForm<HistoryFormData>({
    defaultValues: DEFAULT_FORM_VALUES,
    resolver,
    mode: 'onChange'
  });

  const watchTitle = useWatch({ control, name: 'title' });

  const { data: historyData, isLoading } = useHistory(historyId);
  const createMutation = useCreateHistory();
  const updateMutation = useUpdateHistory();
  const fileUploadMutation = useFileUpload();

  const isBusy = isSubmitting || isUploadingImage || isDraftSaving || isPublishing;

  useEffect(() => {
    if (!(isEditMode && (historyData?.data || existingHistory))) {
      return;
    }

    const history = historyData?.data || existingHistory;
    const descriptionContent = history.description || '';
    const isHtml = /<[a-z][\s\S]*>/i.test(descriptionContent);

    reset({
      year: String(history.year || new Date().getFullYear()),
      title: history.title || '',
      description: isHtml ? descriptionContent : descriptionContent ? `<p>${descriptionContent}</p>` : '',
      imageUrl: history.imageUrl || ''
    });

    setExistingCoverUrl(history.imageUrl || '');
    setLocalPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
    setLocalCoverFile(null);
  }, [isEditMode, historyData, existingHistory, reset]);

  useEffect(() => {
    return () => {
      if (localPreviewUrl) {
        URL.revokeObjectURL(localPreviewUrl);
      }
    };
  }, [localPreviewUrl]);

  const handleBrowseFile = useCallback(() => {
    fileUploadRef.current?.click();
  }, []);

  const handleLocalRemove = useCallback(() => {
    setLocalPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
    setLocalCoverFile(null);
    setValue('imageUrl', existingCoverUrl || '', { shouldValidate: true, shouldDirty: true });
  }, [existingCoverUrl, setValue]);

  const handleCoverFileChange = useCallback(
    (files: File[] | null) => {
      if (!files || files.length === 0) {
        return;
      }

      const file = files[0];
      const objectUrl = URL.createObjectURL(file);
      setLocalPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return objectUrl;
      });

      setLocalCoverFile(file);
      setValue('imageUrl', objectUrl, { shouldValidate: true, shouldDirty: true });
    },
    [setValue]
  );

  const submitWithStatus = async (data: HistoryFormData, status: 'DRAFT' | 'PUBLISH') => {
    const setLoading = status === 'DRAFT' ? setIsDraftSaving : setIsPublishing;
    setLoading(true);

    try {
      let finalImageUrl = data.imageUrl;

      if (localCoverFile) {
        setIsUploadingImage(true);
        const uploadResult = await fileUploadMutation.mutateAsync({ file: localCoverFile, category: 'IMAGE' });
        if (uploadResult.success && uploadResult.data?.fileUrl) {
          finalImageUrl = uploadResult.data.fileUrl;
        } else {
          throw new Error('Image upload failed');
        }
        setIsUploadingImage(false);
      }

      const payload = {
        fileUrl: finalImageUrl || '',
        year: Number(data.year) || new Date().getFullYear(),
        title: data.title.trim() || 'Untitled History',
        description: htmlToPlainText(data.description) || 'No description',
        status
      };

      if (isEditMode && historyId) {
        const result = await updateMutation.mutateAsync({
          historyId,
          data: payload
        });

        if (!result.success) {
          throw new Error('Update failed');
        }

        showToast.success(status === 'PUBLISH' ? 'History published successfully' : 'History saved as draft successfully');
      } else {
        const result = await createMutation.mutateAsync(payload);

        if (!result.success) {
          throw new Error('Creation failed');
        }

        showToast.success(status === 'PUBLISH' ? 'History published successfully' : 'History created as draft successfully');
      }

      navigate('/admin/history');
    } catch (error) {
      console.error('Error saving history:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to save history';
      showToast.error(errorMessage);
      setIsUploadingImage(false);
    } finally {
      setLoading(false);
    }
  };

  const displayCoverImageUrl = localPreviewUrl || existingCoverUrl;

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: currentYear - 1960 + 6 }, (_, i) => {
      const year = String(currentYear + 5 - i);
      return { value: year, label: year };
    });
  }, []);

  const handleBackClick = useCallback(() => {
    if (isDirty || Boolean(localCoverFile)) {
      setShowUnsavedModal(true);
      return;
    }

    navigate('/admin/history');
  }, [isDirty, localCoverFile, navigate]);

  return (
    <div className='space-y-5 p-6 relative'>
      <div className='flex items-center sticky top-0 bg-white z-10 justify-between'>
        <Button
          type='button'
          variant='ghost'
          onClick={handleBackClick}
          disabled={isBusy}
          className='!px-0 !py-0 text-[#047857] font-semibold hover:underline flex items-center gap-2'
        >
          <ChevronLeftIcon className='w-5 h-5' />
          Back to History
        </Button>

        <div className='flex items-center gap-3'>
          <Button
            type='button'
            variant='secondary'
            loading={isDraftSaving}
            disabled={isBusy}
            onClick={() => {
              submitIntentRef.current = 'DRAFT';
              void handleSubmit((formData) => submitWithStatus(formData, 'DRAFT'))();
            }}
          >
            Save Draft
          </Button>
          <Button
            type='button'
            loading={isPublishing || isUploadingImage}
            disabled={isBusy}
            onClick={() => {
              submitIntentRef.current = 'PUBLISH';
              void handleSubmit((formData) => submitWithStatus(formData, 'PUBLISH'))();
            }}
          >
            Publish
          </Button>
        </div>
      </div>

<div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
      <div>
        <Heading level={3} className='leading-[1.1] font-semibold text-[#0F172A]'>
          {isEditMode ? 'Edit History' : 'Create History'}
        </Heading>
        <nav className='mt-2 flex items-center gap-2 text-sm'>
          <button
            type='button'
            onClick={() => navigate('/admin/history')}
            className='text-wine-100 hover:text-wine-200 hover:underline'
          >
            History
          </button>
          {isEditMode ? (
            <>
              <span className='text-gray-400'>/</span>
              <button
                type='button'
                onClick={() => navigate(`/admin/history/${historyId}`)}
                className='text-wine-100 hover:text-wine-200 hover:underline'
              >
                {(watchTitle || '').trim() || 'History'}
              </button>
              <span className='text-gray-400'>/</span>
              <span className='text-gray-600'>Edit</span>
            </>
          ) : (
            <>
              <span className='text-gray-400'>/</span>
              <span className='text-gray-600'>Create</span>
            </>
          )}
        </nav>
      </div>

      {isLoading && isEditMode ? (
        <div className='bg-white rounded-xl shadow-sm border border-gray-200 p-10'>
          <div className='text-center'>
            <div className='inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#047857]' />
            <Paragraph className='mt-4 text-sm text-gray-500'>Loading history...</Paragraph>
          </div>
        </div>
      ) : (
        <form className='space-y-6' onSubmit={(event) => event.preventDefault()}>
          <section className='rounded-xl border border-gray-200 bg-white p-5'>
            <Heading level={4} className='mb-4 text-lg font-semibold text-gray-900'>
              1. Basic Information
            </Heading>
            <div className='grid items-end grid-cols-1 gap-4 md:grid-cols-2'>
              <Controller
                name='year'
                control={control}
                render={({ field }) => (
                  <div>
                    <Label className='mb-2 block text-sm !font-normal'>Year</Label>
                    <CustomSelect
                      id='year'
                      name={field.name}
                      value={field.value}
                      onChange={(value) => field.onChange(Array.isArray(value) ? value[0] ?? '' : value)}
                      onBlur={() => field.onBlur()}
                      options={yearOptions}
                      placeholder='Select Year'
                      disabled={isBusy}
                      isSearchable
                    />
                    {errors.year?.message ? <Paragraph className='mt-1 text-xs text-red-600'>{errors.year.message}</Paragraph> : null}
                  </div>
                )}
              />

              <Controller
                name='title'
                control={control}
                render={({ field }) => (
                  <Input
                    label='Title'
                    required={submitIntentRef.current === 'PUBLISH'}
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.title?.message}
                    $marginbottom='0'
                    placeholder='Enter title'
                    disabled={isBusy}
                  />
                )}
              />
            </div>
          </section>

          <section className='rounded-xl border border-gray-200 bg-white p-5'>
            <Heading level={4} className='mb-4 text-lg font-semibold text-gray-900'>
              2. Cover Image
            </Heading>
            <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
              <div className='rounded-lg border border-gray-200 overflow-hidden bg-gray-50 max-h-[220px]'>
                {displayCoverImageUrl ? (
                  <img src={displayCoverImageUrl} alt='cover image' className='h-full w-full object-cover' />
                ) : (
                  <div className='h-full min-h-[220px] flex items-center justify-center text-sm text-gray-500'>
                    <Paragraph className='text-sm text-gray-500'>No image selected</Paragraph>
                  </div>
                )}
              </div>

              <div>
                <FileUpload
                  ref={fileUploadRef}
                  label=''
                  placeholder=''
                  $maxSizeMB={10}
                  multiple={false}
                  showPreview={false}
                  allowedFileTypes={['image/*']}
                  onChange={handleCoverFileChange}
                  error={errors.imageUrl?.message}
                  disabled={isBusy}
                  containerClassName='border border-dashed border-gray-300 min-h-[220px]'
                >
                  <div className='flex h-full min-h-[180px] flex-col items-center justify-center gap-2 text-center'>
                    <Paragraph className='text-sm font-semibold text-[#334155]'>Upload cover image</Paragraph>
                    <Paragraph className='text-xs'>Drag and drop or click to browse</Paragraph>
                    <Paragraph className='text-xs'>Recommended: (JPG, PNG, WEBP)</Paragraph>
                    <Paragraph className='text-xs'>Max file size: 10MB</Paragraph>
                  </div>
                </FileUpload>
                <div className='mt-4 flex items-center gap-3'>
                  <Button type='button' variant='secondary' onClick={handleBrowseFile} disabled={isBusy}>
                    Select Image
                  </Button>
                  {localCoverFile ? (
                    <Button type='button' variant='danger' onClick={handleLocalRemove} disabled={isBusy}>
                      Remove
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>
          </section>

          <section className='rounded-xl border border-gray-200 bg-white p-5'>
            <Heading level={4} className='mb-4 text-lg font-semibold text-gray-900'>
              3. Description
            </Heading>
            <Controller
              name='description'
              control={control}
              render={({ field }) => (
                <RichTextEditor
                  value={field.value || ''}
                  onChange={field.onChange}
                  disabled={isBusy}
                  placeholder='Write history description...'
                  className='border-gray-200'
                  toolbarContainerClassName='bg-gray-50 border-gray-200 px-4 py-2'
                  contentAreaClassName='min-h-[220px] max-h-[460px] p-3'
                />
              )}
            />
            {errors.description?.message ? <Paragraph className='mt-1 text-xs text-red-600'>{errors.description.message}</Paragraph> : null}
          </section>
        </form>
      )}
</div>
      <SaveDraftModal
        isOpen={showUnsavedModal}
        onClose={() => setShowUnsavedModal(false)}
        onConfirm={() => {
          setShowUnsavedModal(false);
          submitIntentRef.current = 'DRAFT';
          void handleSubmit((data) => submitWithStatus(data, 'DRAFT'))();
        }}
        onDiscard={() => {
          setShowUnsavedModal(false);
          navigate('/admin/history');
        }}
        title='Save History?'
        subtitle='You have unsaved changes. Save as draft before leaving?'
        confirmButtonText='Save as Draft'
        discardButtonText='Discard'
        isLoading={isDraftSaving}
      />
    </div>
  );
};

export default HistoryEditor;
